import { useState, useContext } from 'react';
import { config, manageConfig } from '../config'
import { User } from "oidc-client-ts"
import { useAuth } from "react-oidc-context";
import GlobalContext from '../store/global-context'
import { getUrlParams, getValidationMessagesModalContent } from '../components/shared/helpers'
import { useApi } from 'usbc-web-react-library';

const useManageApi = () => {
    const [response, setResponse] = useState(null);
    const [isSuccess, setIsSuccess] = useState(null);

    function getAccessToken(){
        const user = getUser();
        const token = user?.access_token;
        return token;
    }
    const {
        Get: GetApi,
        Post: PostApi,
        loading,
        error,
        setError,
    } = useApi(manageConfig.baseUrl, getAccessToken);

    const auth = useAuth();

    const globalCtx = useContext(GlobalContext);

    function loadId(){
        return post({ url: `loadId`, params: getUrlParams()});
    }
    
    function exitAdminView(){
        return post({ url: `exitAdminView`});
    }

    function getAspTrainingAvailable(){
        return get({ url: `getAspTrainingAvailable`});
    }

    function getSafeSportTrainingAvailable(){
        return get({ url: `getSafeSportTrainingAvailable`});
    }
    
    function getCompliance({onError}) {
        return get({ url: `getCompliance`, onError: onError});
    }

    function getMinorAthleteTraining({onError}){
        return get({url:`getMinorAthleteTraining`, onError: onError});
    }

    function submitMinorAthleteTraining({body, onError}){
        return post({url:`submitMinorAthleteTraining`, body: body, onError: onError});
    }

    function registerWithSafeSport({params = null, onError}){
        return post({url: `registerWithSafeSport`, params: params, onError: onError})
    }
    
    function getTermsAndConditions({params = null, onError}){
        return get({url:`getTermsAndConditions`, params: params, onError: onError});
    }

    function acceptTermsAndConditions({params = null, onError}){
        return post({url:`acceptTermsAndConditions`, params: params, onError: onError});
    }

    function getTrainingStepByStep(params){
        return get({url: `getTrainingStepByStep`, params: params});
    }

    function loadCoursePlayerUrl({onError}){
        return get({url: `loadCoursePlayerUrl`, onError: onError});
    }
    
    function getCertificates({onError}){
        return get({url:`getCertificates`, onError: onError, params: getUrlParams()});
    }

    function viewSafeSportCertificate({params = null}){
        return getFileUrl({url:`viewSafeSportCertificate`, params: params});
    }

    function downloadSafeSportCertificate({params = null}){
        return download({url:`viewSafeSportCertificate`, params: params});
    }

    const get = ({
        url,
        params = null,
        headers = null,
        popupValidation = false,
        popupError = false,
        onError = undefined
    }) => {
        return GetApi({ url: url, params: params, headers: headers })
            .then((response) => {
                return parseRowResponse(response);
            })
            .then((responseJson) => {
                return parseJsonResponse(responseJson, popupValidation, popupError, onError);
            });
    };

    const post = ({
        url,
        params = null,
        body = null,
        headers = null,
        popupValidation = false,
        popupError = false,
        onError = undefined
    }) => {
        return PostApi({ url: url, params: params, body: body, headers: headers })
            .then((response) => {
                return parseRowResponse(response);
            })
            .then((responseJson) => {
                return parseJsonResponse(responseJson, popupValidation, popupError, onError);
            });
    };

    const getFileUrl = async ({
        url,
        params = null,
        body = null,
        headers = null,
        popupValidation = false,
        popupError = false,
        onError = null
    }) => {
        return PostApi({ url: url, params: params, body: body, headers: headers })
            .then(async (response) => {
                if(response.ok){
                    let blob = await parseRowResponse(response, true);
                    return URL.createObjectURL(blob);
                }

                return parseRowResponse(response).then((responseJson) => {
                        return parseJsonResponse(responseJson, popupValidation, popupError, onError);
                    }
                )

            });
    };

    const download = ({
        url,
        params = null,
        body = null,
        headers = null,
        popupValidation = false,
        popupError = false,
        onError = null
    }) => {
        return PostApi({ url: url, params: params, body: body, headers: headers })
            .then(async (response) => {
                if(response.ok){
                    let blob = await parseRowResponse(response, true);
                    // Create a URL for the blob.
                    const url = window.URL.createObjectURL(blob);

                    // Create a temporary link element to trigger the download.
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = currentFileName; // Provide the desired file name.
                    a.click();

                    // Clean up the URL and link.
                    window.URL.revokeObjectURL(url);
                    return;
                }

                return parseRowResponse(response).then((responseJson) => {
                    return parseJsonResponse(responseJson, popupValidation, popupError, onError);
                })
            });
    };

    let currentFileName = "";
    function parseRowResponse(response, isFileResponse = false) {
        if (response.status === 401) {
            auth.signinRedirect({
                state: window.location.href,
                extraQueryParams: { app_path: window.location.pathname }
            });
        } else if (response.status === 403) {
            window.location.href = config.selectHubUrl;
        } else {
            if (isFileResponse === false || !response.ok) {
                return response.json();
            } else {
                const contentDisposition = response.headers.get("content-disposition");
                const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
                currentFileName = fileNameMatch ? fileNameMatch[1] : "certificate.pdf";

                return response.blob();
            }
        }
    }

    function parseJsonResponse(responseJson,
        popupValidation = false,
        popupError = false,
        onError = undefined) {
        if (responseJson.isSuccess) {
            setResponse(responseJson.data);
            setIsSuccess(true);
            return {data: responseJson.data, isSuccess: true};
        } else {
            let errors = [...responseJson.validationErrors, ...responseJson.errors]
            setError(errors);
            setIsSuccess(false);
            if (onError) {
                onError(errors);
            }

            if (popupValidation === true &&
                responseJson.validationErrors !== null &&
                responseJson.validationErrors.length > 0) {
                let validationModal = getValidationMessagesModalContent(responseJson.validationErrors);
                globalCtx.showModal(validationModal);
            }

            if (popupError === true &&
                responseJson.errors !== null &&
                responseJson.errors.length > 0) {
                let errorModal = getValidationMessagesModalContent(responseJson.errors);
                globalCtx.showModal(errorModal);
            }
        }
    }

    return {
        loadId,
        exitAdminView,
        getAspTrainingAvailable,
        getSafeSportTrainingAvailable,
        getMinorAthleteTraining,
        submitMinorAthleteTraining,
        getCompliance,
        registerWithSafeSport,
        getTermsAndConditions,
        acceptTermsAndConditions,
        getTrainingStepByStep,
        loadCoursePlayerUrl,
        getCertificates,
        viewSafeSportCertificate,
        downloadSafeSportCertificate,
        response, loading, error, isSuccess
    };
};

export function getUser() {
    const oidcStorage = localStorage.getItem(`oidc.user:${config.oidcConfig.authority}:${config.oidcConfig.client_id}`)
    if (!oidcStorage) {
        return null;
    }

    return User.fromStorageString(oidcStorage);
}

export default useManageApi;

